import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, MapPin, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { School } from '../types';

interface SchoolsSectionProps {
  schools: School[];
}

const SchoolsSection: React.FC<SchoolsSectionProps> = ({ schools }) => {
  const { t } = useTranslation();
  const { getTextInCurrentLanguage } = useLanguage();

  const getRatingColor = (rating: number): string => {
    if (rating >= 9) return 'bg-green-600';
    if (rating >= 7) return 'bg-blue-600';
    if (rating >= 5) return 'bg-yellow-600';
    return 'bg-orange-600';
  };

  const getRatingLabel = (rating: number): string => {
    if (rating >= 9) return t('school.excellent');
    if (rating >= 7) return t('school.good');
    if (rating >= 5) return t('school.average');
    return t('school.belowAverage');
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex items-center gap-2 mb-6">
        <GraduationCap className="h-6 w-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900">{t('school.nearbySchools')}</h2>
      </div>

      <div className="space-y-4">
        {schools.map((school) => (
          <div
            key={school.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{getTextInCurrentLanguage(school.name)}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                    {school.type}
                  </span>
                  <span>{t('school.grades')} {school.grades}</span>
                </div>
              </div>

              {/* Rating Badge */}
              <div className="flex flex-col items-center">
                <div className={`${getRatingColor(school.rating)} text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg`}>
                  {school.rating}
                </div>
                <span className="text-xs text-gray-500 mt-1">{getRatingLabel(school.rating)}</span>
              </div>
            </div>

            {/* School Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{school.distance} {t('school.miAway')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="h-4 w-4 text-gray-400" />
                <span>{school.studentCount.toLocaleString()} {t('school.students')}</span>
              </div>
              <div className="text-gray-500 text-xs md:col-span-1">
                {school.address}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 mt-4">
        {t('school.disclaimer')}
      </p>
    </div>
  );
};

export default SchoolsSection;
